import {
  buttonOrange,
  buttonWhite,
  orangeShades,
  primaryColors,
  transparentColors,
  whiteShades
} from '@assets/styles/helpers/colors.styles'
import { floatingSurface } from '@/shared/styles/floatingSurface.styles'

export const selectStyles = {
  // Chips stay on a single row; they scroll inside their own container
  // (see the tagScroller in Select.tsx) so they never slide under the
  // clear/expand icons, which sit in the input's right padding.
  '& .MuiAutocomplete-inputRoot': {
    flexWrap: 'nowrap',
    overflowX: 'hidden'
  },
  '.MuiFormLabel-root': {
    color: primaryColors.white,
    fontFamily: 'helvetica-neue-lt-pro-cond, sans-serif',
    fontWeight: 900,
    textTransform: 'uppercase',
    fontSize: '0.875rem'
  },
  '.MuiOutlinedInput-root': {
    color: primaryColors.white,
    '&.MuiInputBase-sizeSmall': {
      paddingLeft: '0.25rem'
    }
  },

  '.MuiInputBase-input': {
    fontSize: '0.875rem',
    fontFamily: 'helvetica-neue-lt-pro-cond, sans-serif',
    fontWeight: 400,
    textTransform: 'capitalize',
    minWidth: 0,
    width: 0,
    '&.MuiAutocomplete-input': {
      paddingTop: '0.125rem !important',
      paddingBottom: '0.125rem !important'
    },

    '&::placeholder': {
      color: whiteShades.shade1,
      opacity: 1,
      fontFamily: 'helvetica-neue-lt-pro-cond, sans-serif',
      fontWeight: 400,
      textTransform: 'capitalize'
    }
  },
  '.MuiOutlinedInput-input': {
    height: '1.125rem'
  },
  '.MuiAutocomplete-tag': {
    fontFamily: 'helvetica-neue-lt-pro-cond, sans-serif',
    fontWeight: 900,
    textTransform: 'capitalize',
    fontSize: '0.75rem',
    lineHeight: '0.75rem'
  },
  '.MuiAutocomplete-clearIndicator': {
    padding: '0 !important'
  }
}

/*
  Higher than `selectStyles`' own input rules by one class, so it wins without
  replacing them.
*/
export const keepCaseStyles = {
  '& .MuiInputBase-input.MuiAutocomplete-input': {
    textTransform: 'none',
    '&::placeholder': {
      textTransform: 'none'
    }
  }
}

export const popperStyles = {
  ...floatingSurface,
  fontFamily: 'helvetica-neue-lt-pro-cond, sans-serif',
  fontWeight: 900,
  textTransform: 'capitalize',
  fontSize: '0.875rem',

  '.MuiTypography-root': {
    fontFamily: 'helvetica-neue-lt-pro-cond, sans-serif',
    fontWeight: 900,
    textTransform: 'capitalize',
    fontSize: '0.875rem'
  },
  '.MuiListItem-root': {
    display: 'flex',
    alignItems: 'center'
  },
  '.MuiListItem-root:hover': {
    '.MuiCheckbox-root': {
      color: buttonOrange.contained.backgroundHover
    },
    '.MuiTypography-root': {
      color: whiteShades.shade1
    }
  },
  '.MuiCheckbox-root': {
    color: primaryColors.orange,
    padding: 0,
    '&[aria-disabled="true"]': {
      color: `${orangeShades.shade1}`,
      '&.Mui-checked': {
        color: `${orangeShades.shade1}`
      }
    },
    '&.Mui-checked': {
      color: `${primaryColors.orange}`
    },
    '&:hover': {
      color: buttonOrange.contained.backgroundHover
    }
  },
  '[data-testid="IndeterminateCheckBoxIcon"]': {
    color: `${primaryColors.orange}`
  },
  '.MuiCheckbox-indeterminate': {
    color: `${primaryColors.orange}`
  },
  '.MuiTouchRipple-root': {
    display: 'none'
  },

  '.MuiAutocomplete-option': {
    '&:hover': {
      color: buttonWhite.text.fontHover
    },
    '&:active': {
      color: buttonWhite.text.fontActive
    },
    '[&aria-selected="true"]': {
      color: whiteShades.shade1
    },
    '&.Mui-focused': {
      backgroundColor: 'transparent !important'
    }
  },
  '.MuiAutocomplete-noOptions': {
    color: whiteShades.shade1
  },
  '.MuiAutocomplete-option[aria-selected="true"]': {
    backgroundColor: 'transparent !important'
  },
  '.MuiAutocomplete-option[aria-selected="true"]:not(:has(.MuiCheckbox-root))':
    {
      backgroundColor: 'transparent !important',
      '&:hover': {
        '.MuiTypography-root': {
          color: whiteShades.shade2
        }
      },
      '.MuiTypography-root': {
        color: whiteShades.shade2
      },
      '&.Mui-focused': {
        backgroundColor: 'transparent'
      }
    },
  '.MuiAutocomplete-option[aria-selected="true"]:hover': {
    color: whiteShades.shade1,
    '.MuiTypography-root': {
      color: whiteShades.shade1
    }
  },

  '.MuiList-padding': {
    '.MuiButtonBase-root': {
      fontFamily: 'helvetica-neue-lt-pro-cond, sans-serif',
      fontWeight: 900,
      textTransform: 'capitalize',
      fontSize: '0.875rem'
    }
  }
}

export const chipStyles = {
  backgroundColor: transparentColors.whiteShade1,
  borderRadius: '0.125rem',
  color: primaryColors.white,
  fontFamily: 'helvetica-neue-lt-pro-cond, sans-serif',
  fontWeight: 900,
  textTransform: 'uppercase',
  '.MuiSvgIcon-root': {
    color: whiteShades.shade1,
    fontSize: '0.875rem',
    // Where a chip has to shrink, the label ellipsis absorbs it; a squashed
    // delete icon would just look broken.
    flexShrink: 0
  },
  '&.MuiAutocomplete-tag': {
    height: '1.125rem'
  }
}

export const listboxStyles = {
  overflowY: 'auto',
  // maxHeight: '9rem',
  margin: '0.25rem 0.25rem',
  '&:hover': {
    '&::-webkit-scrollbar': {
      width: '0.5rem'
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: transparentColors.orangeShade4,
      borderRadius: '0.5rem'
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: transparentColors.transparent,
      borderRadius: '0.5rem'
    }
  },

  '&::-webkit-scrollbar': {
    width: '0.25rem'
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: primaryColors.orange,
    borderRadius: '0.25rem'
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: primaryColors.white,
    borderRadius: '0.25rem'
  }
}

export const listElementStyles = {
  gap: '0.5rem',

  display: 'flex',
  alignItems: 'center',
  '.icon-medium': {
    color: `${primaryColors.orange} !important`
  },
  '&:hover': {
    '.icon-medium': {
      color: `${orangeShades.shade1} !important`
    }
  },
  '&[aria-selected="true"]': {
    '.icon-medium': {
      color: `${orangeShades.shade1} !important`
    }
  }
}
